import { useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { uploadTeamsData } from "./APIService";
import { TEAMS } from "./Constants";

const useStyles = makeStyles({
  dialog: {
    position: "absolute",
    top: 10,
    width: 500,
  },
  spinnerOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    zIndex: 9999,
  },
});

const TEAMS_UPLOAD_SUCCESS = "Teams Data upload Successful";
const VALIDATION_FAILED_MESSAGE =
  "The file failed validation. Please ensure your file meets the required format.";
const EMAILS_NOT_FOUND_MESSAGE =
  "These email addresses were not found in Microsoft. Please correct them and try again.";
const FILE_ACCEPT = ".csv";
const ALLOWED_EXTENSIONS = [".csv"];

/** Only these columns are accepted; all others are ignored. All must be non-empty. */
const REQUIRED_TEAMS_FIELDS = [
  "AccountLegalEntity",
  "Client",
  "Region",
  "BusinessLine",
  "ChannelAgencyReferral",
  "HighProfileOrder",
  "HybridEvent",
  "Email",
];

/** Boolean column: only true/false/Any allowed (stored lowercase except Any). */
const BOOLEAN_TEAMS_FIELDS = new Set(["HighProfileOrder"]);

/**
 * Free-text / enum string columns lowercased for Dynamo case-sensitive contains
 * matching (except Any). HybridEvent is a string (e.g. true/false/not sure), not a boolean.
 */
const LOWERCASE_TEAMS_FIELDS = new Set(["ChannelAgencyReferral", "HybridEvent"]);

/**
 * Normalizes a cell value: trims strings, converts All/Any (any casing) to "Any",
 * removes spaces immediately before or after "#", and removes "#" when
 * it appears at the start or end of the value.
 * - HighProfileOrder: allowlist true/false (lowercase), or Any
 * - ChannelAgencyReferral / HybridEvent: lowercase tokens (except Any)
 */
function normalizeCellValue(val, fieldName) {
  if (typeof val !== "string") return val;
  let s = val.trim();
  s = s.replace(/\s+#/g, "#").replace(/#\s+/g, "#");
  if (s.startsWith("#")) s = s.slice(1);
  if (s.endsWith("#")) s = s.slice(0, -1);
  s = s.trim();
  const lower = s.toLowerCase();
  if (lower === "all" || lower === "any") return "Any";

  if (fieldName && BOOLEAN_TEAMS_FIELDS.has(fieldName)) {
    if (lower === "true" || lower === "false") return lower;
    // Invalid boolean token — blank so required-field validation fails
    return "";
  }

  if (fieldName && LOWERCASE_TEAMS_FIELDS.has(fieldName)) {
    return s
      .split("#")
      .map((part) => part.trim().toLowerCase())
      .join("#");
  }
  return s;
}

/**
 * Parses a single CSV line, handling quoted fields (e.g. "a,b",c).
 */
function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (c === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += c;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Converts CSV text to an array of objects (first row = headers).
 * Trims each cell value (including header names).
 */
function csvToJSON(text) {
  const lines = text.trim().split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]).map((h) => (h ?? "").trim());
  const headerToCanonical = new Map(
    REQUIRED_TEAMS_FIELDS.map((field) => [field.toLowerCase(), field])
  );
  return lines.slice(1).map((line) => {
    const values = parseCSVLine(line);
    const obj = {};
    headers.forEach((h, i) => {
      const canonical = headerToCanonical.get((h || "").toLowerCase()) || h;
      obj[canonical] = normalizeCellValue(values[i] ?? "", canonical);
    });
    return obj;
  });
}

/**
 * Reads a File as text.
 */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}


/**
 * Finds row key matching required field name (case-insensitive).
 */
function getFieldValue(row, fieldName) {
  const key = Object.keys(row).find(
    (k) => (k || "").trim().toLowerCase() === fieldName.toLowerCase()
  );
  return key != null ? String(row[key]).trim() : "";
}

/**
 * Keeps only required columns (ignores others) and validates that required fields are non-empty.
 * Returns { valid: true, data } or { valid: false, message }.
 */
function filterAndValidateTeamsData(rows) {
  const filtered = rows.map((row, index) => {
    const out = {};
    for (const field of REQUIRED_TEAMS_FIELDS) {
      out[field] = getFieldValue(row, field);
    }
    return { row: out, rowNumber: index + 2 }; // +2: 1-based, and row 1 is header
  });

  const firstInvalid = filtered.find(({ row }) =>
    REQUIRED_TEAMS_FIELDS.some((field) => !row[field])
  );
  if (firstInvalid) {
    const missing = REQUIRED_TEAMS_FIELDS.filter((f) => !firstInvalid.row[f]);
    const booleanHint = missing.includes("HighProfileOrder")
      ? " HighProfileOrder must be true, false, or Any."
      : "";
    return {
      valid: false,
      message: `Row ${firstInvalid.rowNumber}: ${missing.join(", ")} must not be empty. All required fields (${REQUIRED_TEAMS_FIELDS.join(", ")}) must have a value.${booleanHint}`,
    };
  }

  return {
    valid: true,
    data: filtered.map(({ row }) => row),
  };
}

export default function TeamsPage() {
  const [uploading, setUploading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorEmailsNotFound, setErrorEmailsNotFound] = useState([]);
  const fileInputRef = useRef(null);
  const classes = useStyles();

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const isAllowed = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
    if (!isAllowed) {
      setErrorMessage("Only CSV files are allowed.");
      setErrorEmailsNotFound([]);
      setErrorOpen(true);
      return;
    }

    setUploading(true);
    setErrorOpen(false);
    setSuccessOpen(false);

    try {
      let jsonData;
      {
        const text = await readFileAsText(file);
        jsonData = csvToJSON(text);
      }
      if (!jsonData || jsonData.length === 0) {
        setErrorMessage("The file is empty or has no data rows.");
        setErrorEmailsNotFound([]);
        setErrorOpen(true);
        return;
      }
      const validated = filterAndValidateTeamsData(jsonData);
      if (!validated.valid) {
        setErrorMessage(validated.message);
        setErrorEmailsNotFound([]);
        setErrorOpen(true);
        return;
      }
      const result = await uploadTeamsData(validated.data);
      if (result.success) {
        setSuccessOpen(true);
      } else {
        setErrorMessage(result.message || VALIDATION_FAILED_MESSAGE);
        setErrorEmailsNotFound(Array.isArray(result.emailsNotFound) ? result.emailsNotFound : []);
        setErrorOpen(true);
      }
    } catch (err) {
      setErrorMessage(err?.message || VALIDATION_FAILED_MESSAGE);
      setErrorEmailsNotFound([]);
      setErrorOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleSuccessClose = () => setSuccessOpen(false);
  const handleErrorClose = () => {
    setErrorOpen(false);
    setErrorEmailsNotFound([]);
  };

  return (
    <div className="oe-connect__root-page">
      <div className="page-container">
        <section className="top-page-title horizontal-row horizontal-row--with-bottom-margin">
          <h1 style={{ paddingTop: "20px", paddingBottom: "10px" }} className="text-ellipsis">
            {TEAMS}
          </h1>
          <div className="horizontal-row__align-to-row-end">
            <div style={{ paddingTop: "15px", paddingBottom: "0px" }} className="buttons-block">
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT}
                onChange={handleFileChange}
                style={{ display: "none" }}
                data-testid="teams-csv-input"
              />
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading}
                className="default-button default-button--fat events-list__button-new"
              >
                <span>Upload</span>
              </button>
            </div>
          </div>
        </section>
      </div>

      {uploading && (
        <div className={classes.spinnerOverlay} role="progressbar" aria-label="Uploading">
          <CircularProgress size={48} />
        </div>
      )}

      <Dialog
        classes={{ paper: classes.dialog }}
        open={successOpen}
        BackdropProps={{
          invisible: false,
          style: { backgroundColor: "#ffffffbf" },
        }}
        onClose={handleSuccessClose}
        aria-labelledby="teams-success-dialog-title"
        aria-describedby="teams-success-dialog-description"
      >
        <div className="oe-popup-layout">
          <div className="oe-popup-layout__body">
            <div className="alert-body">
              <span className="alert-message" id="teams-success-dialog-description">
                {TEAMS_UPLOAD_SUCCESS}
              </span>
            </div>
          </div>
          <div className="oe-popup-layout__footer">
            <div className="buttons-block buttons-block--from-right">
              <button
                type="button"
                onClick={handleSuccessClose}
                className="default-button default-button--fat default-button--confirm-safe"
              >
                <span>Okay</span>
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        classes={{ paper: classes.dialog }}
        open={errorOpen}
        BackdropProps={{
          invisible: false,
          style: { backgroundColor: "#ffffffbf" },
        }}
        onClose={handleErrorClose}
        aria-labelledby="teams-error-dialog-title"
        aria-describedby="teams-error-dialog-description"
      >
        <div className="oe-popup-layout">
          <div className="oe-popup-layout__body">
            <div className="alert-body">
              <span
                className="alert-message alert-message--danger"
                id="teams-error-dialog-description"
              >
                {errorMessage}
              </span>
              {errorMessage === EMAILS_NOT_FOUND_MESSAGE &&
                errorEmailsNotFound.length > 0 && (
                  <div
                    style={{ color: "#0066cc", marginTop: "12px", fontSize: "14px" }}
                    aria-label="Emails not found"
                  >
                    {errorEmailsNotFound.join(", ")}
                  </div>
                )}
            </div>
          </div>
          <div className="oe-popup-layout__footer">
            <div className="buttons-block buttons-block--from-right">
              <button
                type="button"
                onClick={handleErrorClose}
                className="default-button default-button--fat default-button--cancel"
              >
                <span>Okay</span>
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
